import React from 'react';

import { Spinner } from '@chakra-ui/spinner';
import { useDisclosure } from '@chakra-ui/hooks';
import { Center, Flex, Stack, StackProps, Text } from '@chakra-ui/layout';

import { MdApartment, MdUnfoldMore } from 'react-icons/md';

import { COLLAPSED_RAIL_WIDTH } from './NavItem';

import { AnimatePresence, motion } from 'framer-motion';

import { useQuery } from 'react-query';
import { fetchWashHubs } from 'api/washhub';

import { useIsSuperAdmin, washHub } from 'store/slices';
import { useAppDispatch } from 'store/hooks';
import getSortedData from 'utils/getSortedData';
import { EventRegister } from 'utils';

const queryKey = 'wash-hub-locations';

const TransferHubWashhub = `6552e877b476bf1a0f578a06`;

const MotionStack = motion<StackProps>(Stack as any);

type WashHubSelectorProps = {
  /** Collapsed rail: show the icon only, with the hub name as a tooltip. */
  isCollapsed?: boolean;
};

export const WashHubSelector = ({
  isCollapsed = false,
}: WashHubSelectorProps) => {
  const dispatch = useAppDispatch();
  const isSuperAdmin = useIsSuperAdmin();
  const selectedLocation = washHub.selectors.useSelectedWashHub();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data = [], isLoading } = useQuery(queryKey, fetchWashHubs, {
    onSuccess: (res) => {
      const hubs = res.filter((hub) => hub._id !== TransferHubWashhub);

      dispatch(washHub.WashhubActions.saveLocations(hubs));
      if (selectedLocation == null) {
        dispatch(washHub.WashhubActions.setSelectedLocation(hubs?.[0]));
      } else if (hubs?.length === 1) {
        dispatch(washHub.WashhubActions.setSelectedLocation(hubs?.[0]));
      }
    },
  });

  const location = selectedLocation?.name?.slice(0, undefined) || '';

  if (isSuperAdmin === false) {
    return <Flex h="100px" />;
  }

  const filteredWashhubs = data.filter((hub) => hub._id !== TransferHubWashhub);

  if (isSuperAdmin) {
    // Manually add 'Global View' to the washhub selection menu
    filteredWashhubs.unshift({
      createdAt: new Date(),
      name: 'Global View',
      packages: [],
      size: '',
      type: 'wash_hub',
      updatedAt: '',
      _id: '',
      admin: '',
      code: '',
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        _id: '',
      },
    });
  }

  return (
    <Flex w="100%" direction="column" position="relative" mb={4}>
      {!isCollapsed && (
        <Stack mt={4} fontWeight="bold" color="#445C4E">
          <Text fontSize="12px">SELECT WASH HUB</Text>
        </Stack>
      )}
      <Flex
        px={2}
        py={2}
        mt={isCollapsed ? 4 : 1}
        bg="#000"
        w="100%"
        color="#fff"
        align="center"
        borderRadius="10px"
        cursor="pointer"
        title={isCollapsed ? location : undefined}
        justify={isCollapsed ? 'center' : 'space-between'}
        onClick={isOpen ? onClose : onOpen}>
        {isLoading ? (
          <Center w="100%">
            <Spinner />
          </Center>
        ) : isCollapsed ? (
          <MdApartment size="22px" />
        ) : (
          <>
            <Stack align="center" isInline>
              <MdApartment size="22px" />
              <Text userSelect="none" fontWeight="bold">
                {location}
              </Text>
            </Stack>
            <MdUnfoldMore size="22px" />
          </>
        )}
      </Flex>
      <AnimatePresence>
        {isOpen && (
          <>
            <Flex
              flex={1}
              position="fixed"
              top={0}
              bottom={0}
              left={0}
              right={0}
              bg="transparent"
              zIndex={0}
              onClick={onClose}
            />
            {/* In the rail this has to escape the sidebar, whose ancestors
                clip overflow — hence fixed rather than absolute. */}
            <MotionStack
              mt={1}
              px={2}
              py={2}
              w={isCollapsed ? '220px' : '100%'}
              zIndex={1400}
              top={isCollapsed ? '140px' : 20}
              left={isCollapsed ? COLLAPSED_RAIL_WIDTH : undefined}
              shadow="lg"
              bg="#7A7A7A"
              position={isCollapsed ? 'fixed' : 'absolute'}
              borderRadius="10px"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ type: 'tween' }}
              maxH="500px"
              //@ts-ignore
              overflowY="overlay"
              css={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#ccc',
                  borderRadius: '24px',
                },
              }}>
              {getSortedData([...filteredWashhubs])?.map((l) => (
                <Text
                  key={l._id}
                  pl="10px"
                  color="#fff"
                  cursor="pointer"
                  _hover={{
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    if (selectedLocation?._id === l._id) {
                      onClose();

                      return;
                    }

                    EventRegister.emit('wash_hub_change', l._id);

                    dispatch(washHub.WashhubActions.setSelectedLocation(l));
                    onClose();
                  }}>
                  {l.name}
                </Text>
              ))}
            </MotionStack>
          </>
        )}
      </AnimatePresence>
    </Flex>
  );
};
